import React, { useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { apiClient } from "@/api/client"
import { WarehouseDto, WarehouseCreateDto, WarehouseUpdateDto } from "@/api/api-client"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetTrigger,
} from "@/components/ui/sheet"
import { Plus, Trash2, Pencil, Building2, MapPin } from "lucide-react"
import { toastSuccess, toastError } from "@/lib/toast"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { warehouseSchema, type WarehouseFormData } from "@/lib/validationSchemas"
import { ScrollArea } from "@/components/ui/scroll-area"
import { useLanguage } from "@/hooks/useLanguage"
import { AddressFields } from "@/components/profile/AddressFields"

export const WarehouseManagement: React.FC = () => {
  const queryClient = useQueryClient()
  const { t } = useLanguage()
  const text = t.warehouseManagement
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const [editingWarehouse, setEditingWarehouse] = useState<WarehouseDto | null>(null)

  const { data: warehouses, isLoading } = useQuery({
    queryKey: ["warehouses"],
    queryFn: () => apiClient.getAllWarehouses(),
  })

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
    control,
  } = useForm<WarehouseFormData>({
    resolver: zodResolver(warehouseSchema),
  })

  const createMutation = useMutation({
    mutationFn: (data: WarehouseFormData) => {
      const dto: WarehouseCreateDto = {
        name: data.name,
        address: data.address as any, 
      };
      return apiClient.createWarehouse(dto)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["warehouses"] })
      setIsSheetOpen(false)
      reset()
      toastSuccess(text.successCreate)
    },
    onError: (error: any) => {
       toastError(error.message || text.errorCreate);
    }
  })

  const updateMutation = useMutation({
    mutationFn: (data: WarehouseFormData) => {
      const dto: WarehouseUpdateDto = {
        name: data.name,
        address: data.address as any,
      };
      return apiClient.updateWarehouse(editingWarehouse!.id, dto);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["warehouses"] })
      setIsSheetOpen(false)
      setEditingWarehouse(null)
      reset()
      toastSuccess(text.successUpdate)
    },
    onError: (error: any) => {
      toastError(error.message || text.errorUpdate);
   }
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiClient.deleteWarehouse(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["warehouses"] })
      toastSuccess(text.successDelete)
    },
    onError: (error: any) => {
       toastError(error.message || text.errorDelete);
    }
  })

  const onSubmit = (data: WarehouseFormData) => {
    if (editingWarehouse) {
      updateMutation.mutate(data)
    } else {
      createMutation.mutate(data)
    }
  }

  const handleEdit = (warehouse: WarehouseDto) => {
    setEditingWarehouse(warehouse)
    setValue("name", warehouse.name)
    
    // Explicitly set each address field
    if (warehouse.address) {
      setValue("address.street", warehouse.address.street || "")
      setValue("address.externalNumber", warehouse.address.externalNumber || "")
      setValue("address.internalNumber", warehouse.address.internalNumber || "")
      setValue("address.neighborhood", warehouse.address.neighborhood || "")
      setValue("address.city", warehouse.address.city || "")
      setValue("address.state", warehouse.address.state || "")
      setValue("address.zipCode", warehouse.address.zipCode || "")
      setValue("address.country", warehouse.address.country || "México")
    }
    
    setIsSheetOpen(true)
  }

  return (
    <div className="space-y-10">
      <div className="flex flex-col items-start justify-between gap-6 border-b-2 border-foreground/5 pb-8 md:flex-row md:items-end">
        <div>
          <h2 className="mb-2 font-display text-4xl leading-none tracking-tighter text-foreground">
            {text.title}
          </h2>
          <p className="text-sm font-medium uppercase tracking-widest text-muted-foreground">
            {text.subtitle}
          </p>
        </div>

        <Sheet
          open={isSheetOpen}
          onOpenChange={(open) => {
            setIsSheetOpen(open)
            if (!open) {
              setEditingWarehouse(null)
              reset()
            }
          }}
        >
          <SheetTrigger asChild>
            <Button className="h-12 rounded-lg bg-primary px-8 font-display tracking-widest text-primary-foreground shadow-md hover:opacity-90">
              <Plus className="mr-2 h-4 w-4" />
              {text.newWarehouse}
            </Button>
          </SheetTrigger>
          <SheetContent className="flex h-full w-full flex-col overflow-hidden border-l border-border bg-muted p-0 shadow-2xl sm:max-w-[500px]">
            <div className="flex h-full flex-col px-1">
              <SheetHeader className="border-b bg-background p-6">
                <SheetTitle className="font-display text-2xl tracking-widest">
                  {editingWarehouse ? text.editWarehouse : text.newWarehouse}
                </SheetTitle>
              </SheetHeader>

              <ScrollArea className="flex-1">
                <div className="p-6">
                  <form id="warehouse-form" onSubmit={handleSubmit(onSubmit)} className="space-y-8">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="label-industrial">
                        {text.nameLabel}
                      </Label>
                      <Input id="name" {...register("name")} className="h-12 text-base" placeholder={text.namePlaceholder} />
                      {errors.name && (
                        <p className="text-xs font-medium text-destructive">
                          {errors.name.message}
                        </p>
                      )}
                    </div>
                    
                    <div className="pt-4 border-t border-border">
                        <Label className="label-industrial mb-6 block">{text.addressLabel}</Label>
                        <AddressFields register={register} errors={errors} control={control} setValue={setValue} prefix="address" />
                    </div>
                  </form>
                </div>
              </ScrollArea>

              <SheetFooter className="border-t bg-muted/10 p-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsSheetOpen(false)}
                  className="font-display tracking-widest"
                >
                  {text.cancel}
                </Button>
                <Button
                  type="submit"
                  form="warehouse-form"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="h-12 bg-primary px-8 font-display tracking-widest text-primary-foreground shadow-md hover:opacity-90"
                >
                  {createMutation.isPending || updateMutation.isPending ? "..." : text.save}
                </Button>
              </SheetFooter>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      <div className="border-none bg-transparent">
        <Table>
          <TableHeader>
            <TableRow className="border-b-2 border-border hover:bg-transparent">
              <TableHead className="px-0 font-display text-lg tracking-widest text-foreground">
                {text.nameCol}
              </TableHead>
              <TableHead className="font-display text-lg tracking-widest text-foreground">
                {text.locationCol}
              </TableHead>
              <TableHead className="px-0 text-right font-display text-lg tracking-widest text-foreground">
                {text.actionsCol}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={3} className="py-20 text-center">
                  <div className="animate-pulse font-display text-2xl tracking-widest opacity-20">
                    {text.loading}
                  </div>
                </TableCell>
              </TableRow>
            ) : warehouses && warehouses.length > 0 ? (
              warehouses.map((warehouse) => (
                <TableRow
                  key={warehouse.id}
                  className="group border-b border-border transition-colors hover:bg-muted/5"
                >
                  <TableCell className="px-0 py-6">
                    <div className="flex items-center gap-4">
                      <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-lg border border-border bg-muted/50 grayscale transition-all duration-500 group-hover:grayscale-0">
                          <Building2 className="h-6 w-6 text-muted-foreground/30" />
                      </div>
                      <span className="font-display text-xl tracking-wider text-foreground transition-colors group-hover:text-accent">
                        {warehouse.name}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="max-w-md font-medium text-muted-foreground">
                    <div className="flex items-start gap-2">
                        <MapPin className="h-4 w-4 mt-1 flex-shrink-0 opacity-50" />
                        <span>
                            {warehouse.address.street} {warehouse.address.externalNumber}
                            {warehouse.address.internalNumber ? ` Int ${warehouse.address.internalNumber}` : ""},<br/>
                            {warehouse.address.neighborhood}, {warehouse.address.city}, {warehouse.address.state} {warehouse.address.zipCode}
                        </span>
                    </div>
                  </TableCell>
                  <TableCell className="px-0 py-6 text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Editar almacén"
                        className="transition-colors hover:bg-accent/10 hover:text-accent"
                        onClick={() => handleEdit(warehouse)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Eliminar almacén"
                        className="text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                        onClick={() => {
                          if (confirm(text.deleteConfirm)) {
                            deleteMutation.mutate(warehouse.id)
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} className="empty-state py-32">
                  <Building2 className="mx-auto mb-4 h-12 w-12 text-muted-foreground opacity-10" />
                  <p className="font-display text-2xl tracking-widest text-muted-foreground opacity-20">
                    {text.empty}
                  </p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
